import {Component, computed, effect, inject, LOCALE_ID, Signal} from '@angular/core';
import {formatDate} from '@angular/common';

import {
  Breadcrumb,
  BreadcrumbsComponent,
  InfoModalComponent,
  ModalContentComponent,
  SelectButtonComponent,
  SvgSpriteIcon,
} from '@selpia/shared/ui';
import {AppConfigFacade} from '@selpia/app-config/data-access';
import {OptionsFacade, OptionsUpdateFacade} from '@selpia/options/data-access';

import {PagePath} from '../page-path.model';
import {FeatureDisplaySelectorComponent} from '@selpia/options/feature-display-selector';
import {FeatureKpiComponent} from '@selpia/data-batch/feature-kpi';
import {MonitoringService} from '@selpia/user/tracking';
import {FeatureInsightsComponent} from '@data-batch/feature-insights';

@Component({
  selector: 'web-home-view',
  imports: [FeatureKpiComponent, InfoModalComponent, ModalContentComponent, BreadcrumbsComponent, SelectButtonComponent, FeatureDisplaySelectorComponent, FeatureInsightsComponent],
  templateUrl: './home-view.component.html',
  styleUrl: './home-view.component.scss',
})
export class HomeViewComponent {
  readonly #locale = inject(LOCALE_ID);

  readonly #appConfigFacade = inject(AppConfigFacade);
  readonly #optionsFacade = inject(OptionsFacade);
  readonly #optionsUpdateFacade = inject(OptionsUpdateFacade);
  readonly #monitoringService = inject(MonitoringService);

  readonly isAIAssistantEnabled = this.#appConfigFacade.isAIAssistantEnabled;

  constructor() {
    this.#setDefaultPeriod();
  }

  protected readonly SvgSpriteIcon = SvgSpriteIcon;

  protected readonly tags: Signal<Breadcrumb[]> = computed(() => {
    const levelTag = {name: 'Total Company', code: 'total'};

    const date = this.#appConfigFacade.initialDate();
    const period = this.#optionsFacade.focusPeriod();
    if (!date || !period) {
      return [levelTag];
    }
    const dateTag = {name: `${formatDate(date, "MMM", this.#locale)} ${period}`, code: 'date'};

    return [levelTag, dateTag];
  });

  protected readonly availablePeriods: Signal<string[]> = this.#appConfigFacade.periods;
  protected readonly focusPeriod: Signal<string | undefined> = this.#optionsFacade.focusPeriod;

  #setDefaultPeriod() {
    const e1 = effect(() => {
      const focusPeriod = this.focusPeriod();

      if (!focusPeriod) {
        this.#optionsUpdateFacade.setOptions({focusPeriod: this.#appConfigFacade.initialPeriod()});
        e1.destroy();
      }
    }, {manualCleanup: true});
  }

  updateFocusPeriod(period: string | undefined) {
    if (period) {
      this.#optionsUpdateFacade.setOptions({focusPeriod: period});
      this.#monitoringService.logEvent('PeriodChanged', {period: period, view: "Dashboard"});
    }
  }

  protected readonly PagePath = PagePath;
}
